import { flow, getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import { RootStoreModel } from "~models/root-store"
import { withEnvironment } from "../extensions/with-environment"

function toNonNegativeInt(value: unknown): number {
  if (typeof value === "number" && !Number.isNaN(value)) return Math.max(0, Math.floor(value))
  if (typeof value === "string") {
    const n = parseInt(value, 10)
    if (!Number.isNaN(n)) return Math.max(0, n)
  }
  return 0
}

/** 与 getUserUnreadNotificationCount 返回的 data 结构一致 */
function applyUnreadCountData(
  self: {
    transaction_order: number
    pending_claim: number
    system_message: number
    userUnreadNotificationCount: number
  },
  data: unknown,
) {
  if (!data || typeof data !== "object") {
    self.transaction_order = 0
    self.pending_claim = 0
    self.system_message = 0
    self.userUnreadNotificationCount = 0
    return
  }
  const o = data as Record<string, unknown>
  self.transaction_order = toNonNegativeInt(o.transaction_order)
  self.pending_claim = toNonNegativeInt(o.pending_claim)
  self.system_message = toNonNegativeInt(o.system_message)
  if ("total" in o && o.total !== undefined && o.total !== null) {
    self.userUnreadNotificationCount = toNonNegativeInt(o.total)
  } else {
    self.userUnreadNotificationCount =
      self.transaction_order + self.pending_claim + self.system_message
  }
}

export const NotificationStoreModel = types
  .model("NotificationStore")
  .props({
    /** 交易订单类未读（后端 type 1） */
    transaction_order: types.optional(types.number, 0),
    /** 待领取类未读（后端 type 2） */
    pending_claim: types.optional(types.number, 0),
    /** 系统消息类未读（后端 type 3） */
    system_message: types.optional(types.number, 0),
    /** 未读总数，与接口 total 一致 */
    userUnreadNotificationCount: types.optional(types.number, 0),
  })
  .extend(withEnvironment)
  .actions((self) => {
    const { authStore, apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)

    const resetUserUnreadNotificationCount = () => {
      applyUnreadCountData(self, null)
    }

    const refreshUserUnreadNotificationCount = flow(function* () {
      if (!authStore.hasToken) {
        resetUserUnreadNotificationCount()
        return
      }
      try {
        const res = yield apiStore.getUserUnreadNotificationCount({ params: {} })
        if (res?.data) {
          applyUnreadCountData(self, res.data)
        }
      } catch {
        // 保留上一次成功的数据
      }
    })

    return {
      resetUserUnreadNotificationCount,
      refreshUserUnreadNotificationCount,
    }
  })

type NotificationStoreType = Instance<typeof NotificationStoreModel>
export interface NotificationStore extends NotificationStoreType {}
type NotificationStoreSnapshotType = SnapshotOut<typeof NotificationStoreModel>
export interface NotificationStoreSnapshot extends NotificationStoreSnapshotType {}
